import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetCalendarMeetingSummaryQuery } from '../../api/calendar.js';
import IconChecked from '../../components/images/IconChecked.jsx';
import BaseOverlay from '../../components/layout/BaseOverlay.jsx';
import BaseButton from '../form/BaseButton.jsx';
import BaseCard from './BaseCard.jsx';

const AfterMeetingStep4 = ({ meetingId, showThirdOverlay }) => {
    const navigate = useNavigate();

    const {
        data: meetingSummary,
        error: fetchError,
        isLoading: isLoadingSummary
    } = useGetCalendarMeetingSummaryQuery(meetingId, {
        skip: !showThirdOverlay
    });

    const renderObjectives = () => {
        const objectives = meetingSummary?.response?.objectives || [];

        if (objectives.length === 0) {
            return <p className='text-theme1-title2 -mt-1'>No objectives found</p>;
        }

        return objectives.map((objective) => (
            <div key={objective.id} className='flex justify-between py-1 w-full'>
                <span className={`text-theme1-title2 text-[16px] max-w-[90%] ${objective.is_met ? 'font-bold' : ''}`}>
                    {objective.text}
                </span>
                <span className='flex space-x-4 items-start mt-[3px]'>
                    {objective.is_met ? <IconChecked className='w-8 h-8 fill-theme1-title2' /> : ''}
                </span>
            </div>
        ));
    };

    const renderParticipants = () => {
        const participants = Object.values(meetingSummary?.response?.participants || {});

        if (participants.length === 0) {
            return <p className='text-theme1-title2 -mt-1'>No participants found</p>;
        }

        return participants.map((participant) => (
            <div key={participant.id} className='py-1'>
                <span className='text-theme1-title2 font-bold text-[16px]'>
                    {`${participant.contact.first_name || ''} ${participant.contact.last_name || ''}`.trim()}
                </span>

                {participant.follow_up && (
                    <div className='flex'>
                        <div className='text-theme1-title2 flex-shrink-0'>Follow up:</div>
                        <div
                            className='text-theme1-primary ml-2'
                            onClick={() => navigate('/calendar/edit/' + participant.calendar_meeting_id)}
                        >
                            {participant.calendar_meeting_title}
                        </div>
                    </div>
                )}
            </div>
        ));
    };

    return (
        <BaseOverlay show={showThirdOverlay} className='overflow-auto h-[100dvh] rounded-none'>
            <div className='min-h-96 max-w-3xl w-full mx-auto px-8'>
                <div className='flex items-center gap-[12px] mt-10'>
                    <div className='rounded-full bg-theme1-primary w-[36px] h-[36px] flex items-center justify-center text-white text-xl'>
                        4
                    </div>
                    <div>
                        <h4 className='font-bold text-base'>Step 4</h4>
                        <p className='text-sm text-theme1-title2'>Summary</p>
                    </div>
                </div>
                <BaseCard className='mt-6 mb-6 p-6 bg-theme1-white'>
                    <div>
                        <h5 className='font-bold text-lg mb-1 text-theme1-primary'>Objectives</h5>
                        {isLoadingSummary ? 'Loading...' : renderObjectives()}
                    </div>

                    <div className='mt-5'>
                        <h5 className='font-bold text-lg mb-1 text-theme1-primary'>Participants</h5>
                        {isLoadingSummary ? 'Loading...' : renderParticipants()}
                    </div>

                    <div className='mt-5'>
                        <h5 className='font-bold text-lg mb-1 text-theme1-primary'>Actions</h5>
                        <p
                            className={`text-theme1-title2 text-[16px] whitespace-pre-wrap ${!meetingSummary?.response?.actions ? '-mt-1' : ''}`}
                        >
                            {meetingSummary?.response?.actions || 'No actions defined'}
                        </p>
                    </div>
                </BaseCard>
                <BaseButton
                    className='mt-4 mb-5 bg-theme1-primary text-white'
                    textValue='Finish'
                    onClick={() => navigate('/')}
                />
            </div>
        </BaseOverlay>
    );
};

export default AfterMeetingStep4;
